import { useRef, useState } from 'react';

import { FormOutlined, HighlightOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { SignatureCanvas } from 'react-signature-canvas';
import { MModal, MTypography } from 'src/lib';
import { MFormItemError } from 'src/lib/MFormItemError/MFormItemError';
import { Color, Spacing } from 'src/styles';
import trimCanvas from 'trim-canvas';

import * as Styles from './UserSignatureOptionList.styles';

export const SIGNATURE_FONT_STYLES = ['Pacifico', 'Satisfy', 'Yellowtail'];
const CUSTOM_SIGNATURE_INDEX = 3;

export type UserSignatureOptionListProps = {
  title?: string;
  value?: HTMLElement;
  error?: string;
  fullName?: string;
  onChange: (signature: HTMLElement) => void;
  testId?: 'primary' | 'joint';
};

export const UserSignatureOptionList = ({
  title,
  fullName,
  value,
  error,
  onChange,
  testId,
}: UserSignatureOptionListProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customSignatureImage, setCustomerSignatureImage] = useState<string>();
  const sigPad = useRef<SignatureCanvas | null>(null);
  const signatureRef = useRef<HTMLElement[]>([]);

  const trimSigPad = () => {
    const canvas = sigPad.current?.getCanvas();
    let copy = document.createElement('canvas');

    if (canvas) {
      copy.width = canvas.width;
      copy.height = canvas.height;
    }
    const context = copy.getContext('2d');

    if (context && canvas) {
      context.drawImage(canvas, 0, 0);

      return trimCanvas(copy);
    }

    return canvas;
  };

  return (
    <>
      <Row>
        <Col span={24} className={Spacing.mt16}>
          <MTypography variant='subtitle1' weight='semibold'>
            {title ?? 'Please select a signature to use'}
          </MTypography>
        </Col>
        <Col span={24}>
          {SIGNATURE_FONT_STYLES.map((font, i) => (
            <div
              data-testid={`${testId}-account-signature-` + i}
              key={`${font}_key`}
              className={Styles.fontSignatureContainer}
              style={{
                fontFamily: font,
                border: value && value === signatureRef.current[i] ? `1px solid ${Color.Brand6}` : '1px solid #DFE5F0',
              }}>
              <div
                className={Styles.signatureContainer}
                onClick={() => {
                  onChange(signatureRef.current[i]);
                }}
                ref={ref => {
                  if (ref) {
                    signatureRef.current[i] = ref;
                  }
                }}>
                {fullName}
              </div>
            </div>
          ))}
          {customSignatureImage && (
            <div
              className={Styles.drawnSignatureContainer}
              style={{
                border:
                  value && value === signatureRef.current[CUSTOM_SIGNATURE_INDEX]
                    ? `1px solid ${Color.Brand6}`
                    : '1px solid #DFE5F0',
              }}>
              <div
                className={Styles.signatureContainer}
                ref={ref => {
                  if (ref) {
                    signatureRef.current[CUSTOM_SIGNATURE_INDEX] = ref;
                  }
                }}
                onClick={() => onChange(signatureRef.current[CUSTOM_SIGNATURE_INDEX])}>
                <img src={customSignatureImage} alt='signature' style={{ height: 40, width: 150 }} />
              </div>
            </div>
          )}
        </Col>
        <Col span={24}>{error && <MFormItemError value={error} />}</Col>
      </Row>

      {Boolean(customSignatureImage) && (
        <div
          onClick={() => {
            setIsModalVisible(true);
          }}
          className={Styles.modifySignatureButton}>
          <HighlightOutlined className={Styles.penStyle} />
          <MTypography variant='body1' color={Color.Brand6} inline>
            Modify Signature
          </MTypography>
        </div>
      )}

      <Row>
        <Col span={24} className={Styles.drawSignature}>
          <MTypography variant='body1' weight='semibold' color={Color.Brand6} align='center'>
            OR
          </MTypography>
        </Col>
        <Col
          span={24}
          onClick={() => {
            setIsModalVisible(true);
          }}
          className={Styles.drawSignature}
          style={{ cursor: 'pointer' }}>
          <FormOutlined className={Styles.quillPen} />
          <MTypography variant='body1' weight='semibold' color={Color.Brand6} inline>
            Draw your signature
          </MTypography>
        </Col>
      </Row>

      <MModal
        title='Draw your signature'
        visible={isModalVisible}
        secondaryButtonText='Clear Signature'
        primaryButtonText='Save Signature'
        onClose={() => {
          setIsModalVisible(false);
        }}
        onPrimaryButtonClick={() => {
          setIsModalVisible(false);
          // NOTE: unable to use trimCanvas() method because it is not found in the module
          // due to webpack compilation issue of the react-signature-canvas library
          const copy = trimSigPad();
          const signature = copy?.toDataURL('image/png');
          setCustomerSignatureImage(signature);
        }}
        onSecondaryButtonClick={() => {
          sigPad.current?.clear();
        }}>
        <div className={Styles.signaturePadContainer}>
          <SignatureCanvas ref={sigPad} penColor='black' canvasProps={{ width: 450, height: 200 }} />
        </div>
      </MModal>
    </>
  );
};
